import { useCallback, useEffect, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { useT } from "../../i18n/LanguageProvider";
import { categoryLabel } from "../../i18n/taxonomy";
import { uomLabel } from "./shopFloorFormat";
import { t as translate } from "../../i18n/t";
import { isShelfZone } from "../../utils/shelfZones";
import { boxContentsSummary } from "../../utils/boxSummary";
import { createPortal } from "react-dom";
import { shopModeApi, type FloorBoxContents } from "../../api/shopMode";
import type { Zone } from "../../api/types";
import { useShopMode } from "../../state/ShopModeContext";
import { ItemThumb } from "../ItemThumb";
import { ShopFloorBoxLabelPrint } from "./ShopFloorBoxLabelPrint";
import { money } from "../../i18n/intl";
import { useDismissOnEscape } from "../../hooks/useDismissOnEscape";

type Panel = "none" | "label" | "take-out" | "edit";

function canRetire(role: string | undefined): boolean {
  // OD-46: retiring is a manager's job. The server enforces it too.
  return role === "OWNER" || role === "ADMIN" || role === "MANAGER";
}

/**
 * G-84 (OD-51) — one box: what is inside, Print Barcode, take things out,
 * rename, retire. Putting things IN is deliberately not here — that happens
 * from the item card after scanning the label (OD-50), so there is one flow
 * for it and the scan is what proves which box received the item.
 *
 * Portalled to the body (the `backdrop-filter` trap, G-73).
 */
export function ShopFloorBoxCard({
  boxId,
  shelfZones,
  onClose,
  onChanged,
  onOpenItem,
}: {
  boxId: string;
  shelfZones: Zone[];
  onClose(): void;
  onChanged?(): void;
  onOpenItem?(itemId: string): void;
}) {
  const { t } = useT();
  const { floorActor } = useShopMode();
  const [contents, setContents] = useState<FloorBoxContents | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>("none");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Take-out panel
  const [takeItemId, setTakeItemId] = useState("");
  const [takeQty, setTakeQty] = useState("1");
  const [takeZoneId, setTakeZoneId] = useState("");
  // Edit panel
  const [nameDraft, setNameDraft] = useState("");
  const [keptDraft, setKeptDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await shopModeApi.floorBoxContents(boxId);
      setContents(next);
      setNameDraft(next.box.name ?? "");
      setKeptDraft(next.box.keptInZone?.id ?? "");
    } catch (err: unknown) {
      setError(errorText(err, "box.couldNotOpen"));
    } finally {
      setLoading(false);
    }
  }, [boxId]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * G-106 — one stack, not one listener each: a box card opens an item card
   * over itself (`onOpenItem`), and two plain listeners closed both on one
   * press.
   */
  useDismissOnEscape(onClose);

  const run = useCallback(
    async (work: () => Promise<void>, doneWords: string) => {
      setBusy(true);
      setActionError(null);
      setNotice(null);
      try {
        await work();
        setPanel("none");
        setNotice(doneWords);
        await load();
        onChanged?.();
      } catch (err: unknown) {
        setActionError(errorText(err, "box.actionFailed"));
      } finally {
        setBusy(false);
      }
    },
    [load, onChanged],
  );

  const shelves = shelfZones.filter((z) => z.isActive && isShelfZone(z));
  const box = contents?.box;
  const chosen = contents?.items.find((i) => i.itemId === takeItemId);

  return createPortal(
    <div
      className="sf-bouquet-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={
        box ? t("box.boxCodeAria", { code: box.code }) : t("box.boxFallbackTitle")
      }
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sf-bouquet-modal sf-box-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sf-item-card-head">
          <h4 className="sf-bouquet-modal-title">
            {loading && !box ? t("box.opening") : box?.code ?? t("box.boxFallbackTitle")}
            {box?.name ? <span className="sf-box-modal-subtitle"> · {box.name}</span> : null}
          </h4>
          <button type="button" className="button plain" onClick={onClose}>
            {t("common.close")}
          </button>
        </div>

        {error ? <div className="alert error">{error}</div> : null}
        {actionError ? <div className="alert error">{actionError}</div> : null}
        {notice ? <div className="alert success">{notice}</div> : null}

        {box ? (
          <>
            <p className="muted sf-box-modal-meta">
              {box.keptInZone
                ? `${t("box.keptInZone", { zone: box.keptInZone.name })} · `
                : ""}
              {boxContentsSummary(contents.itemCount, contents.totalUnits)}
              {box.status === "RETIRED" ? ` · ${t("box.retired")}` : ""}
            </p>

            {contents.items.length > 0 ? (
              <ul className="sf-finder-results sf-box-items">
                {contents.items.map((it) => (
                  <li key={it.itemId}>
                    <button
                      type="button"
                      className="sf-finder-result"
                      onClick={() => onOpenItem?.(it.itemId)}
                      disabled={!onOpenItem}
                    >
                      <ItemThumb itemId={it.itemId} imageKey={it.imageKey} alt={it.name} />
                      <span className="sf-finder-result-main">
                        <span className="sf-finder-result-name">{it.name}</span>
                        <span className="sf-finder-result-where">
                          {it.qtyOnHand} {uomLabel(it.defaultUom, Number(it.qtyOnHand))} ·{" "}
                        {categoryLabel(it.categoryCode, it.categoryLabel)}
                        </span>
                      </span>
                      <span className="sf-finder-result-price">
                        {it.saleUnitPriceEur ? money(it.saleUnitPriceEur) : t("itemFinder.priceUnknown")}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">
                {t("box.emptyBoxHint")}
              </p>
            )}

            {panel === "none" ? (
              <div className="sf-item-card-actions">
                <button
                  type="button"
                  className="button shop-floor-action-btn"
                  disabled={busy}
                  onClick={() => setPanel("label")}
                >
                  {t("box.printBarcode")}
                </button>
                {contents.items.length > 0 && box.status === "ACTIVE" ? (
                  <button
                    type="button"
                    className="button shop-floor-action-btn"
                    disabled={busy}
                    onClick={() => {
                      setActionError(null);
                      setTakeItemId(contents.items[0].itemId);
                      setTakeQty("1");
                      setTakeZoneId(shelves[0]?.id ?? "");
                      setPanel("take-out");
                    }}
                  >
                    {t("box.takeOut")}
                  </button>
                ) : null}
                {box.status === "ACTIVE" ? (
                  <button
                    type="button"
                    className="button shop-floor-action-btn"
                    disabled={busy}
                    onClick={() => {
                      setActionError(null);
                      setPanel("edit");
                    }}
                  >
                    {t("box.rename")}
                  </button>
                ) : null}
                {box.status === "ACTIVE" && canRetire(floorActor?.role) ? (
                  <button
                    type="button"
                    className="button plain"
                    disabled={busy || contents.items.length > 0}
                    title={contents.items.length > 0 ? t("box.takeEverythingOutFirst") : undefined}
                    onClick={() =>
                      void run(async () => {
                        await shopModeApi.floorRetireBox(box.id);
                      }, translate("box.retiredNotice"))
                    }
                  >
                    {t("box.retireBox")}
                  </button>
                ) : null}
              </div>
            ) : null}

            {panel === "label" ? (
              <div className="sf-item-card-panel">
                <ShopFloorBoxLabelPrint box={box} />
                <div className="sf-item-card-panel-actions">
                  <button type="button" className="button plain" onClick={() => setPanel("none")}>
                    {t("box.back")}
                  </button>
                </div>
              </div>
            ) : null}

            {panel === "take-out" ? (
              <div className="sf-item-card-panel">
                <label htmlFor="sf-box-take-item">{t("box.whichItem")}</label>
                <select
                  id="sf-box-take-item"
                  value={takeItemId}
                  onChange={(e) => {
                    setTakeItemId(e.target.value);
                    setTakeQty("1");
                  }}
                >
                  {contents.items.map((it) => (
                    <option key={it.itemId} value={it.itemId}>
                      {it.name} ({it.qtyOnHand})
                    </option>
                  ))}
                </select>
                <label htmlFor="sf-box-take-qty">{t("box.howMany")}</label>
                <input
                  id="sf-box-take-qty"
                  type="number"
                  inputMode="decimal"
                  min={0.001}
                  max={chosen ? Number(chosen.qtyOnHand) : undefined}
                  step="any"
                  value={takeQty}
                  onChange={(e) => setTakeQty(e.target.value)}
                />
                <label htmlFor="sf-box-take-zone">{t("box.putItOn")}</label>
                <select
                  id="sf-box-take-zone"
                  value={takeZoneId}
                  onChange={(e) => setTakeZoneId(e.target.value)}
                >
                  {shelves.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
                </select>
                <div className="sf-item-card-panel-actions">
                  <button type="button" className="button plain" disabled={busy} onClick={() => setPanel("none")}>
                    {t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    className="button shop-floor-action-btn"
                    disabled={busy || !takeItemId || !takeZoneId || !(Number(takeQty) > 0)}
                    onClick={() =>
                      void run(async () => {
                        await shopModeApi.floorTakeOutOfBox(box.id, {
                          itemId: takeItemId,
                          quantity: Number(takeQty),
                          toZoneId: takeZoneId,
                        });
                      }, translate("box.takenOut"))
                    }
                  >
                    {t("box.takeOut")}
                  </button>
                </div>
              </div>
            ) : null}

            {panel === "edit" ? (
              <div className="sf-item-card-panel">
                <label htmlFor="sf-box-name">{t("box.name")}</label>
                {/*
                  G-98 (owner 2026-09-20): the box's own code, as a PLACEHOLDER
                  rather than a value. A florist opening Rename sees what the
                  box is already called and types over it only if they want
                  something better; leaving it blank stores nothing. Writing
                  the code into the field instead would render "BOX-000017 ·
                  BOX-000017" on the card, the list and the printed label, and
                  would make a box nobody named indistinguishable from one
                  somebody deliberately named after its code.
                */}
                <input
                  id="sf-box-name"
                  type="text"
                  maxLength={80}
                  placeholder={box.code}
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  autoComplete="off"
                />
                <label htmlFor="sf-box-kept">{t("box.keptIn")}</label>
                <select id="sf-box-kept" value={keptDraft} onChange={(e) => setKeptDraft(e.target.value)}>
                  <option value="">{t("box.notRecorded")}</option>
                  {shelves.map((z) => (
                    <option key={z.id} value={z.id}>
                      {z.name}
                    </option>
                  ))}
                </select>
                <div className="sf-item-card-panel-actions">
                  <button type="button" className="button plain" disabled={busy} onClick={() => setPanel("none")}>
                    {t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    className="button shop-floor-action-btn"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await shopModeApi.floorUpdateBox(box.id, {
                          name: nameDraft.trim() || null,
                          keptInZoneId: keptDraft || null,
                        });
                      }, "Saved.")
                    }
                  >
                    {t("box.save")}
                  </button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
